import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { formatDateInBusinessTZ } from '../../../utils/date';
import { getErrorMessage } from '../../../utils/errorMessage';
import {
  type AdminCart,
  CART_LIST_SELECT,
  cartOwedCents,
  cartReceivedCents,
  cartStatusLabel,
  payerName,
} from '../../utils/carts';

type CartView = 'open_zelle' | 'all';

function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/**
 * Bundle payments (payment carts). Opens on the queue an admin actually works: Zelle codes
 * still taking money -- who has one, for how much, how much has arrived, until when.
 */
export function CartsPanel({ onOpenCart }: { onOpenCart: (cartId: string) => void }) {
  const [view, setView] = useState<CartView>('open_zelle');
  const [carts, setCarts] = useState<AdminCart[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setCarts(null);
    setError(null);
    (async () => {
      let query = supabase.from('payment_carts').select(CART_LIST_SELECT);
      if (view === 'open_zelle') {
        query = query
          .eq('kind', 'zelle')
          .in('status', ['open', 'partially_funded'])
          .gt('expires_at', new Date().toISOString());
      }
      const { data, error: loadError } = await query.order('created_at', { ascending: false }).limit(100);
      if (!live) return;
      if (loadError) setError(getErrorMessage(loadError));
      else setCarts((data ?? []) as unknown as AdminCart[]);
    })();
    return () => { live = false; };
  }, [view]);

  return (
    <div className="p-4 sm:p-6" data-testid="carts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-600">
          One payment for several invoices. Each invoice&apos;s share is also listed under Payments.
        </p>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group">
          {([['open_zelle', 'Open Zelle codes'], ['all', 'All']] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`px-3 py-1.5 text-sm ${view === value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              aria-pressed={view === value}
              onClick={() => setView(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      ) : carts === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : carts.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="carts-empty">
          {view === 'open_zelle' ? 'No Zelle codes are waiting for money.' : 'No bundle payments yet.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2">Opened</th>
                <th className="px-4 py-2">Parent</th>
                <th className="px-4 py-2">How</th>
                <th className="px-4 py-2 text-right">Invoices</th>
                <th className="px-4 py-2 text-right">Target</th>
                <th className="px-4 py-2 text-right">Received</th>
                <th className="px-4 py-2 text-right">Still owed</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {carts.map((cart) => (
                <tr
                  key={cart.id}
                  className="cursor-pointer hover:bg-blue-50"
                  onClick={() => onOpenCart(cart.id)}
                  data-testid={`cart-row-${cart.id}`}
                >
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">{formatDateInBusinessTZ(cart.created_at)}</td>
                  <td className="px-4 py-2">
                    <div className="text-gray-900">{payerName(cart)}</div>
                    {cart.parent?.email ? <div className="text-xs text-gray-500">{cart.parent.email}</div> : null}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {cart.kind === 'zelle' ? <span className="font-mono">{cart.memo_code}</span> : cart.kind === 'ach' ? 'ACH' : 'Card'}
                  </td>
                  <td className="px-4 py-2 text-right">{(cart.payment_cart_items ?? []).length}</td>
                  <td className="px-4 py-2 text-right">{usd(cart.target_amount_cents)}</td>
                  <td className="px-4 py-2 text-right">{usd(cartReceivedCents(cart))}</td>
                  <td className="px-4 py-2 text-right">{usd(cartOwedCents(cart))}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">{cartStatusLabel(cart)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
